<template>
  <div
    class="tab-pane fade"
    :class="{ 'show active': isActive }"
    id="nav-event-highlights"
    role="tabpanel"
    aria-labelledby="nav-event-highlights-tab"
  >
    <div class="event-highlights">
      <ul
        id="highlights-pills-tab"
        class="nav nav-pills highlights-tabs"
        role="tablist"
      >
        <li class="nav-item">
          <a
            id="highlights-tab"
            class="nav-link active"
            data-toggle="pill"
            href="#highlights-pane"
            role="tab"
            aria-controls="highlights-pane"
            aria-selected="true"
          >
            Highlights ({{ highlights.length }})
          </a>
        </li>
        <li class="nav-item">
          <a
            id="purchased-tab"
            class="nav-link"
            data-toggle="pill"
            href="#purchased-pane"
            role="tab"
            aria-controls="purchased-pane"
            aria-selected="false"
          >
            Purchased ({{ purchased.length }})
          </a>
        </li>
      </ul>

      <div class="tab-content highlights-tab-content">
        <div
          id="highlights-pane"
          class="tab-pane fade show active"
          role="tabpanel"
          aria-labelledby="highlights-tab"
        >
          <p class="highlights-note">
            Note: Each video can be downloaded within 30 days of the activity
            upload date. Once purchased and downloaded, they remain in your
            library for 30 days from the purchase date.
          </p>
          <div class="row" v-if="highlights.length">
            <div
              class="col-12 col-sm-6 col-md-4"
              v-for="(video, i) of highlights"
              :key="i"
            >
              <highlight-card
                :video="video"
                @play="openPlayer"
                @download="confirmDownload"
              />
            </div>
          </div>
          <p v-else class="empty-state">No highlights available.</p>
        </div>

        <div
          id="purchased-pane"
          class="tab-pane fade"
          role="tabpanel"
          aria-labelledby="purchased-tab"
        >
          <p class="highlights-note">
            Note: Each purchased video remains in your library for 30 days from
            the purchase date.
          </p>
          <div class="row" v-if="purchased.length">
            <div
              class="col-12 col-sm-6 col-md-4"
              v-for="(video, i) of purchased"
              :key="i"
            >
              <highlight-card
                :video="video"
                purchased
                @play="openPlayer"
                @download="confirmRedownload"
                @delete="confirmDelete"
              />
            </div>
          </div>
          <p v-else class="empty-state">No purchased videos.</p>
        </div>
      </div>

      <highlight-video-player
        v-if="activeVideo"
        :video="activeVideo"
        @close="activeVideo = null"
        @download="confirmDownload"
      />

      <highlight-dialog
        v-if="confirmVideo"
        title="Download Video"
        @close="confirmVideo = null"
      >
        <p class="hl-confirm-text">
          Downloading this video will cost {{ confirmVideo.points }} Gems.<br />
          Do you want to continue?
        </p>
        <div class="hl-confirm-box">
          <div class="hl-confirm-cost">
            <img src="/assets/event-highlights/gems.svg" alt="" />
            <span>{{ confirmVideo.points }}</span>
          </div>
          <div v-if="canAfford" class="hl-confirm-balance">
            Gems Balance: {{ gems }}
          </div>
          <div v-else class="hl-confirm-balance is-error">
            <i class="fas fa-exclamation-circle"></i>
            Insufficient Gems Balance: {{ gems }}
          </div>
        </div>
        <template #actions>
          <button
            type="button"
            class="hl-btn hl-btn--cancel"
            @click="confirmVideo = null"
          >
            <span>Cancel</span>
          </button>
          <button
            v-if="canAfford"
            type="button"
            class="hl-btn hl-btn--download"
            @click="doDownload"
          >
            <span>Download</span>
          </button>
          <button
            v-else
            type="button"
            class="hl-btn hl-btn--buygems"
            @click="buyGems"
          >
            <span>Buy Gems</span>
          </button>
        </template>
      </highlight-dialog>

      <highlight-dialog
        v-if="redownloadVideo"
        title="Redownload Video"
        @close="redownloadVideo = null"
      >
        <p class="hl-confirm-text">
          Are you sure you want to redownload this video
        </p>
        <template #actions>
          <button
            type="button"
            class="hl-btn hl-btn--cancel"
            @click="redownloadVideo = null"
          >
            <span>Cancel</span>
          </button>
          <button
            type="button"
            class="hl-btn hl-btn--download"
            @click="doRedownload"
          >
            <span>Download</span>
          </button>
        </template>
      </highlight-dialog>

      <highlight-dialog
        v-if="deleteVideo"
        title="Delete Video"
        @close="deleteVideo = null"
      >
        <p class="hl-confirm-text">
          Are you sure you want to delete this video
        </p>
        <template #actions>
          <button
            type="button"
            class="hl-btn hl-btn--cancel"
            @click="deleteVideo = null"
          >
            <span>Cancel</span>
          </button>
          <button type="button" class="hl-btn hl-btn--danger" @click="doDelete">
            <span>Delete</span>
          </button>
        </template>
      </highlight-dialog>
    </div>
  </div>
</template>

<script>
import HighlightCard from "./HighlightCard";
import HighlightDialog from "./HighlightDialog";
import HighlightVideoPlayer from "./HighlightVideoPlayer";
import "@/assets/css/event-highlights.css";

// Static gem cost per download — keep until the API drives this.
const HIGHLIGHT_COST = 5000;
// Highlights expire 30 days after the activity upload date.
const EXPIRY_DAYS = 30;
const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function parseDate(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) return isNaN(value) ? null : value;
  // MongoDB extended JSON: { $date: "..." } or { $date: { $numberLong: "..." } }
  if (typeof value === "object") {
    const inner = value.$date;
    if (inner && typeof inner === "object" && inner.$numberLong != null) {
      const d = new Date(Number(inner.$numberLong));
      return isNaN(d) ? null : d;
    }
    if (typeof inner === "string" || typeof inner === "number") {
      const d = new Date(inner);
      return isNaN(d) ? null : d;
    }
    return null;
  }
  const d = new Date(value);
  return isNaN(d) ? null : d;
}

function formatDate(value) {
  const d = parseDate(value);
  if (!d) return "";
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${day},${MONTHS[d.getUTCMonth()]},${d.getUTCFullYear()}`;
}

function addDays(value, days) {
  const d = parseDate(value);
  if (!d) return null;
  d.setUTCDate(d.getUTCDate() + days);
  return d;
}

// Map a user_highlights document (from /whoosh/events/highlights) to the
// shape HighlightCard / HighlightVideoPlayer expects.
function mapHighlight(doc) {
  const base = (doc.videoUrl || "").replace(/\/+$/, "");
  return {
    id: doc._id,
    title: doc.eventTitle || doc.eventId || "Highlight",
    thumbnail: "",
    videoUrl: doc.s3VideoKeyWatermark
      ? `${base}/${doc.s3VideoKeyWatermark}`
      : "",
    downloadUrl: doc.s3VideoKeyFinal ? `${base}/${doc.s3VideoKeyFinal}` : "#",
    points: HIGHLIGHT_COST,
    eventDate: formatDate(doc.createdAt),
    expiryDate: formatDate(addDays(doc.createdAt, EXPIRY_DAYS)),
  };
}

export default {
  name: "EventHighlights",
  components: { HighlightCard, HighlightDialog, HighlightVideoPlayer },
  props: {
    isActive: { type: Boolean, default: false },
    // current player gems balance (from rider_default_data via PlayerActivities)
    gemsBalance: { type: Number, default: 0 },
  },
  emits: ["buy-gems"],
  data() {
    return {
      activeVideo: null,
      confirmVideo: null,
      redownloadVideo: null,
      deleteVideo: null,
      // local mirror of the gemsBalance prop, decremented optimistically on
      // download until the backend (which deducts via Java) is wired up
      gems: this.gemsBalance,
      // populated in mounted() from user/event_highlights;
      // purchased starts empty and grows as the user downloads
      highlights: [],
      purchased: [],
    };
  },
  computed: {
    canAfford() {
      if (!this.confirmVideo) return false;
      return this.gems >= this.confirmVideo.points;
    },
    overlayOpen() {
      return (
        !!this.activeVideo ||
        !!this.confirmVideo ||
        !!this.redownloadVideo ||
        !!this.deleteVideo
      );
    },
  },
  watch: {
    gemsBalance(val) {
      this.gems = val;
    },
    overlayOpen(open) {
      document.body.style.overflow = open ? "hidden" : "";
    },
  },
  async mounted() {
    await this.loadHighlights();
  },
  beforeDestroy() {
    document.body.style.overflow = "";
  },
  methods: {
    async loadHighlights() {
      const docs = await this.$store.dispatch("user/event_highlights");
      this.highlights = (docs || []).map(mapHighlight);
    },
    openPlayer(video) {
      this.activeVideo = video;
    },
    confirmDownload(video) {
      this.confirmVideo = video;
    },
    doDownload() {
      if (!this.confirmVideo || !this.canAfford) return;
      const video = this.confirmVideo;

      // TODO: call backend (POST /whoosh/events/highlights/download) which
      // deducts gems via Java; for now mirror the cost locally + move the video.
      this.gems -= video.points;
      const idx = this.highlights.indexOf(video);
      if (idx !== -1) {
        this.highlights.splice(idx, 1);
        this.purchased.push(video);
      }

      this.confirmVideo = null;
      this.activeVideo = null;
      this.redownload(video);
    },
    redownload(video) {
      if (video.downloadUrl && video.downloadUrl !== "#") {
        window.open(video.downloadUrl, "_blank", "noopener");
      }
    },
    confirmRedownload(video) {
      this.redownloadVideo = video;
    },
    doRedownload() {
      if (!this.redownloadVideo) return;
      const video = this.redownloadVideo;
      this.redownloadVideo = null;
      this.redownload(video);
    },
    buyGems() {
      this.confirmVideo = null;
      // let the parent (PlayerActivities) switch to the In Game Shop tab
      this.$emit("buy-gems");
    },
    confirmDelete(video) {
      this.deleteVideo = video;
    },
    doDelete() {
      if (!this.deleteVideo) return;

      // TODO: call backend to delete the purchased download — on success:
      const idx = this.purchased.indexOf(this.deleteVideo);
      if (idx !== -1) this.purchased.splice(idx, 1);

      this.deleteVideo = null;
    },
  },
};
</script>

<style scoped>
/* standalone panel on the player-activities page (no longer wrapped by the
   edit-profile .login-box, so it brings its own surface + spacing) */
#nav-event-highlights {
  padding: 24px 0 40px;
}
.event-highlights {
  background: #fff;
  border-radius: 12px;
  padding: 28px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}
@media (max-width: 575.98px) {
  .event-highlights {
    padding: 16px;
  }
}
.highlights-tabs {
  background: #e8e8e8;
  border-radius: 10px;
  padding: 6px;
  margin-bottom: 20px;
}
.highlights-tabs .nav-item {
  flex: 1 1 50%;
  text-align: center;
}
.highlights-tabs .nav-link {
  color: #1a1a1a;
  font-weight: 700;
  font-size: 18px;
  border-radius: 8px;
  padding: 12px 0;
}
.highlights-tabs .nav-link.active {
  background: #000;
  color: #fff;
}
.highlights-note {
  background: #efefef;
  border-radius: 8px;
  text-align: center;
  padding: 14px;
  margin-bottom: 20px;
  font-size: 16px;
  color: #333;
}
.empty-state {
  text-align: center;
  padding: 30px;
  color: #818181;
}
</style>
